import ReactECharts from 'echarts-for-react';
import { useMemo, useRef } from 'react';
import { MeasurementMarkerDto, SessionOptionDto } from 'types/models/SessionsPageData';
import { t } from 'utilities/TranslationUtils';
import { KICK_RESULT_BUCKET_COLORS, KICK_RESULT_Z, SCATTER_SYMBOL_SIZE, kickMarkerColor, measurementResultBucket } from 'utilities/chartUtils';
import { useDragRectSelect, clampModalX, clampModalY } from './useDragRectSelect';
// Round 21: same Z flip helper the measurement-detail page uses — match the live/3D convention.
import { soccerZToChartZ } from 'pages/MeasurementDetail/coords';

const GOAL_WIDTH = 7.32;
const GOAL_HEIGHT = 2.44;
const MARGIN_SIDE = 1.5;
const MARGIN_TOP = 0.8;  // reduced 20% from 1.0

type Props = {
  data: MeasurementMarkerDto[];
  sessions: SessionOptionDto[];
  selectedIds: ReadonlySet<string>;
  hoveredId: string | null;
  onPointClick: (id: string, clientX: number, clientY: number) => void;
  onPointHover: (id: string | null, clientX?: number, clientY?: number) => void;
  onDragSelected: (measurementIds: string[]) => void;
  hasActiveFilters?: boolean;
  onClearFilters?: () => void;
  showSelectionState?: boolean;
  dragSelectionEnabled?: boolean;
  performanceMode?: boolean;
};

const SessionsGoalChart = ({ data, sessions, selectedIds, hoveredId, onPointClick, onPointHover, onDragSelected, hasActiveFilters, onClearFilters, showSelectionState = true, dragSelectionEnabled = true, performanceMode = false }: Props): React.ReactElement => {
  const containerRef = useRef<HTMLDivElement>(null);
  const echartsRef = useRef<ReactECharts>(null);

  // Round 21: derive (chartZ, chartY, id, raw measurement) tuples up-front with Z flipped to match
  // the live/3D convention. All downstream series + the drag-select hit-test consume this list.
  const points = useMemo(() => data
    .filter((m) => m.goalY !== null && m.goalZ !== null)
    .map((m) => ({ id: m.id, z: soccerZToChartZ(m.goalZ as number), y: m.goalY as number, result: m.result, raw: m })),
    [data],
  );
  const emptyMessage = data.length === 0
    ? t('sessions.goalViewEmptyFiltered')
    : t('sessions.goalViewEmptyNoCrossing');
  const drag = useDragRectSelect({
    points,
    getId: (p) => p.id,
    getPixel: (p, instance) => instance.convertToPixel({ gridIndex: 0 }, [p.z, p.y]) as [number, number] | null,
    containerRef,
    echartsRef,
    onPointerHover: onPointHover,
  });
  const markedIds = useMemo(() => new Set(drag.pending?.ids ?? []), [drag.pending]);

  const halfW = GOAL_WIDTH / 2;
  const yMin = -0.3;
  const yMax = GOAL_HEIGHT + MARGIN_TOP;
  const goalFrameLines = [
    [{ coord: [-halfW, yMin] }, { coord: [-halfW, GOAL_HEIGHT] }],  // left side — up to crossbar
    [{ coord: [halfW, yMin] }, { coord: [halfW, GOAL_HEIGHT] }],    // right side — up to crossbar
    [{ coord: [-halfW, GOAL_HEIGHT] }, { coord: [halfW, GOAL_HEIGHT] }],  // top of goal
    [{ coord: [-(halfW + MARGIN_SIDE), yMin] }, { coord: [halfW + MARGIN_SIDE, yMin] }],  // bottom edge
  ];

  const isMarked = (id: string) => (showSelectionState && selectedIds.has(id)) || markedIds.has(id);
  const markedBorder = 'rgba(37,99,235,0.95)';

  const markerColor = (measurement: MeasurementMarkerDto): string => {
    return kickMarkerColor(measurement);
  };

  const goals = points.filter((p) => measurementResultBucket(p.raw) === 'goal');
  const onTargets = points.filter((p) => measurementResultBucket(p.raw) === 'onTarget');
  const offTargets = points.filter((p) => measurementResultBucket(p.raw) === 'offTarget');
  const onTargetLegendLabel = useMemo(() => {
    if (onTargets.length === 0) return t('sessions.legendOnTarget');
    const kinds = new Set(onTargets.map((p) => (p.result ?? '').toLowerCase()));
    const onlySaved = kinds.size === 1 && kinds.has('saved');
    const onlySavable = kinds.size === 1 && kinds.has('savable');
    const allSavedLike = kinds.size > 0 && Array.from(kinds).every((v) => v === 'saved' || v === 'savable');
    if (onlySaved || allSavedLike) return t('sessions.kickResult_Saved');
    if (onlySavable) return t('sessions.kickResult_Savable');
    return t('sessions.legendOnTarget');
  }, [onTargets]);

  const animateScatter = {
    animation: true,
    animationDuration: 220,
    animationEasing: 'cubicOut' as const,
    animationDelay: (dataIndex: number) => dataIndex * 4,
    animationDurationUpdate: 160,
    animationEasingUpdate: 'cubicOut' as const,
  };

  /** Markline endpoints / markArea coords flow through these callbacks too — guard against non-tuple data. */
  const idFrom = (params: any): string | null => {
    const d = params?.data;
    if (!Array.isArray(d) || d.length < 3) return null;
    const id = d[2];
    return typeof id === 'string' ? id : null;
  };
  const sizeOf = (params: any) => {
    const id = idFrom(params);
    return id && isMarked(id) ? SCATTER_SYMBOL_SIZE + 6 : SCATTER_SYMBOL_SIZE;
  };

  const series: any[] = [
    {
      type: 'scatter',
      name: t('sessions.legendOther'),
      data: offTargets.map((m) => [m.z, m.y, m.id, markerColor(m.raw)]),
      symbolSize: sizeOf,
      itemStyle: {
        color: (params: any) => params?.data?.[3] ?? KICK_RESULT_BUCKET_COLORS.offTarget,
        borderColor: (params: any) => { const id = idFrom(params); return id && isMarked(id) ? markedBorder : 'rgba(255,255,255,0.95)'; },
        borderWidth: (params: any) => { const id = idFrom(params); return id && isMarked(id) ? 3 : 1; },
      },
      z: KICK_RESULT_Z.offTarget,
      ...animateScatter,
      markLine: {
        silent: true, symbol: 'none',
        lineStyle: { color: '#374151', width: 3, type: 'solid' },
        data: goalFrameLines, label: { show: false }, animation: false,
      },
      markArea: {
        silent: true,
        // Extends to yMin so the interior fill covers the full space down to the ground line.
        data: [[{ coord: [-halfW, yMin] }, { coord: [halfW, GOAL_HEIGHT] }]],
        itemStyle: { color: 'rgba(229,231,235,0.25)', borderWidth: 0 },
      },    },
    {
      type: 'scatter',
      name: onTargetLegendLabel,
      data: onTargets.map((m) => [m.z, m.y, m.id, markerColor(m.raw)]),
      symbolSize: sizeOf,
      itemStyle: {
        color: (params: any) => params?.data?.[3] ?? KICK_RESULT_BUCKET_COLORS.onTarget,
        borderColor: (params: any) => { const id = idFrom(params); return id && isMarked(id) ? markedBorder : '#fff'; },
        borderWidth: (params: any) => { const id = idFrom(params); return id && isMarked(id) ? 3 : 1.2; },
      },
      z: KICK_RESULT_Z.onTarget,
      ...animateScatter,
    },
    {
      type: 'scatter',
      name: t('sessions.legendGoal'),
      data: goals.map((m) => [m.z, m.y, m.id, markerColor(m.raw)]),
      symbolSize: sizeOf,
      itemStyle: {
        color: (params: any) => params?.data?.[3] ?? KICK_RESULT_BUCKET_COLORS.goal,
        borderColor: (params: any) => { const id = idFrom(params); return id && isMarked(id) ? markedBorder : '#fff'; },
        borderWidth: (params: any) => { const id = idFrom(params); return id && isMarked(id) ? 3 : 1.5; },
      },
      z: KICK_RESULT_Z.goal,
      ...animateScatter,
    },
  ];

  // Stable pulse series — empty data when no marks, so the previous pulse animation diffs away cleanly on Add/Discard.
  const pulseSeries = [{
    type: 'effectScatter',
    name: 'drag-pulse',
    rippleEffect: { period: 1.4, scale: 3, brushType: 'stroke' as const },
    showEffectOn: 'render' as const,
    data: dragSelectionEnabled && markedIds.size > 0
      ? points.filter((p) => markedIds.has(p.id)).map((p) => [p.z, p.y, p.id])
      : [],
    symbolSize: SCATTER_SYMBOL_SIZE + 6,
    itemStyle: { color: 'rgba(37,99,235,0.95)' },
    z: 6,
    silent: true,
  }];

  // Static glow overlay for persistently-selected kicks (more reliable than callback-driven shadowBlur).
  const selectedGlowSeries = [{
    type: 'scatter',
    name: 'selected-glow',
    data: showSelectionState && selectedIds.size > 0
      ? points
        .filter((p) => selectedIds.has(p.id))
        .sort((a, b) => a.id.localeCompare(b.id))
        .map((p) => ({ id: p.id, value: [p.z, p.y, p.id] }))
      : [],
    symbolSize: SCATTER_SYMBOL_SIZE + 3,
    itemStyle: {
      color: 'rgba(249,115,22,0)',
      borderColor: 'rgb(249,115,22)',
      borderWidth: 2,
    },
    animationDurationUpdate: 120,
    animationEasingUpdate: 'linear',
    z: 5,
    silent: true,
  }];

  // Hover highlight is rendered as a separate non-animated overlay so pointer movement does not
  // restyle/reanimate the entire scatter set.
  const hoveredSeries = [{
    type: 'scatter',
    name: 'hover-glow',
    data: hoveredId
      ? points
        .filter((p) => p.id === hoveredId)
        .map((p) => ({ id: p.id, value: [p.z, p.y, p.id] }))
      : [],
    symbolSize: SCATTER_SYMBOL_SIZE + 6,
    itemStyle: {
      color: 'rgba(37,99,235,0)',
      borderColor: 'rgba(37,99,235,0.95)',
      borderWidth: 3,
    },
    animation: false,
    z: 6,
    silent: true,
  }];

  const option = {
    // Round 17: ECharts tooltip permanently disabled — KickInfoModal is the single info surface.
    tooltip: { show: false },
    legend: { show: false },
    // Light gray fills the full chart canvas (mirrors how SessionsPitchChart uses PITCH_GREEN).
    // The markArea inside the goal posts adds a slightly deeper tint for the goal interior.
    backgroundColor: 'rgba(229,231,235,0.25)',
    // Zero margins on all sides so the plot area's aspect ratio always matches the container's
    // aspect-ratio CSS (see sessions-kick-locations-view--goal in Sessions.scss) — any nonzero,
    // unequal margin here would distort the goal frame / box proportions as the window resizes.
    grid: { left: 0, right: 0, top: 0, bottom: 0, containLabel: false },
    xAxis: {
      type: 'value',
      min: -(halfW + MARGIN_SIDE), max: halfW + MARGIN_SIDE,
      axisLine: { show: false }, axisTick: { show: false }, splitLine: { show: false }, axisLabel: { show: false },
    },
    yAxis: {
      type: 'value',
      min: yMin, max: yMax,
      axisLine: { show: false }, axisTick: { show: false }, splitLine: { show: false }, axisLabel: { show: false },
    },
    series: [...series, ...selectedGlowSeries, ...hoveredSeries, ...pulseSeries],
  };

  const onEvents = {
    click: (params: any) => {
      if (drag.pending) return;
      if (params.seriesType !== 'scatter') return;
      const id = params?.data?.[2];
      if (typeof id !== 'string') return;
      const ev = params.event?.event ?? params.event;
      const clientX = typeof ev?.clientX === 'number' ? ev.clientX : 0;
      const clientY = typeof ev?.clientY === 'number' ? ev.clientY : 0;
      onPointClick(id, clientX, clientY);
    },
    mouseover: (params: any) => {
      if (drag.isDragging || drag.pending) return;
      if (params.seriesType !== 'scatter') return;
      const id = params?.data?.[2];
      if (typeof id !== 'string') return;
      const ev = params.event?.event ?? params.event;
      const clientX = typeof ev?.clientX === 'number' ? ev.clientX : undefined;
      const clientY = typeof ev?.clientY === 'number' ? ev.clientY : undefined;
      onPointHover(id, clientX, clientY);
    },
    globalout: () => onPointHover(null),
  };

  return (
    <div ref={containerRef} className='sessions-pitch-chart-wrapper sessions-pitch-chart-wrapper--goal' {...(dragSelectionEnabled ? drag.handlers : {})}>
      <ReactECharts ref={echartsRef} option={option} style={{ width: '100%', height: '100%' }} onEvents={onEvents} />
      {points.length === 0 && !performanceMode && (
        <div className='sessions-goal-empty-overlay'>
          <p className='chart-empty'>{emptyMessage}</p>
          {hasActiveFilters && onClearFilters && (
            <button
              type='button'
              className='sessions-reset-btn'
              onClick={onClearFilters}
            >
              {t('sessions.clearFiltersEmptyState')}
            </button>
          )}
        </div>
      )}
      {dragSelectionEnabled && drag.dragRect && (
        <div className='sessions-pitch-drag-rect' style={drag.dragRect} />
      )}
      {dragSelectionEnabled && drag.pending && (
        <div
          className='sessions-pitch-pending-modal'
          style={{ left: clampModalX(drag.pending.x, containerRef.current?.clientWidth), top: clampModalY(drag.pending.y, containerRef.current?.clientHeight) }}
          onMouseDown={(e) => e.stopPropagation()}
          onMouseUp={(e) => e.stopPropagation()}
        >
          <button type='button' className='sessions-pitch-pending-add' onClick={() => { onDragSelected(drag.pending!.ids); drag.clearPending(); }}>
            {t('sessions.dragSelectAdd', { count: drag.pending.ids.length })}
          </button>
          <button type='button' className='sessions-pitch-pending-discard' onClick={drag.clearPending}>
            {t('sessions.dragSelectDiscard')}
          </button>
        </div>
      )}
    </div>
  );
};

export default SessionsGoalChart;
